import React, { use, useEffect, useRef, useState } from "react";
import { Header } from "../../Components/Layout/Header.jsx";
import "./ThemeSelection.less";
import { Redirect } from "react-router-dom";

const SunIcon = () => (
    <svg
        xmlns="http://www.w3.org/2000/svg"
        className="casino-theme-icon "
        fill="none"
        viewBox="0 0 24 24"
    >
        <path
            strokeLinecap="round"
            strokeLinejoin="round"
            strokeWidth="2"
            d="M12 3v1m0 16v1m9-9h-1M4 12H3m15.364 6.364l-.707-.707M6.343 6.343l-.707-.707m12.728 0l-.707.707M6.343 17.657l-.707.707M16 12a4 4 0 11-8 0 4 4 0 018 0z"
        />
    </svg>
);

const MoonIcon = () => (
    <svg
        xmlns="http://www.w3.org/2000/svg"
        className="casino-theme-icon "
        fill="none"
        viewBox="0 0 24 24"
    >
        <path
            strokeLinecap="round"
            strokeLinejoin="round"
            strokeWidth="2"
            d="M20.354 15.354A9 9 0 018.646 3.646 9.003 9.003 0 0012 21a9.003 9.003 0 008.354-5.646z"
        />
    </svg>
);

const DeviceIcon = () => (
    <svg
        xmlns="http://www.w3.org/2000/svg"
        className="casino-theme-icon "
        fill="none"
        viewBox="0 0 24 24"
    >
        <path
            strokeLinecap="round"
            strokeLinejoin="round"
            strokeWidth="2"
            d="M9.75 17L9 20l-1 1h8l-1-1-.75-3M3 13h18M5 17h14a2 2 0 002-2V5a2 2 0 00-2-2H5a2 2 0 00-2 2v10a2 2 0 002 2z"
        />
    </svg>
);

const CloseIcon = () => (
    <svg
        xmlns="http://www.w3.org/2000/svg"
        className="casino-theme-icon icon-close"
        fill="none"
        viewBox="0 0 24 24"
    >
        <path
            strokeLinecap="round"
            strokeLinejoin="round"
            strokeWidth="2"
            d="M6 18L18 6M6 6l12 12"
        />
    </svg>
);

const CheckIcon = () => (
    <svg xmlns="http://www.w3.org/2000/svg" className="check-icon" fill="none" viewBox="0 0 24 24" stroke="currentColor">
        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="3" d="M5 13l4 4L19 7" />
    </svg>
);

const ThemeSelection = (props) => {
    const [theme, setTheme] = useState("light");
    const [useDeviceSettings, setUseDeviceSettings] = useState(false);
    const [redirectToHome, setRedirectToHome] = useState(false);
    const [hasLoaded, setHasLoaded] = useState(false);

    const player = props.user?.igracID;
    const darkMode = props.user?.darkMode;
    const savedMode = localStorage.getItem("darkModeStrategy");

    const originalModeRef = useRef(null);
    const savedRef = useRef(false);

    useEffect(() => {
        window.scrollTo(0, 0);
    }, []);

    const applyBodyThemeClass = () => {
        if (typeof window.applyBodyClass === "function") {
            window.applyBodyClass();
            return;
        }
        const href = window.location.href.toLowerCase();
        const isDark = casino.utils.getCasinoTheme() === "dark" && !href.includes("poker");
        if (isDark) {
            document.body.classList.add("dark-mode");
        } else {
            document.body.classList.remove("dark-mode");
        }
    };

    const notifyThemeChange = () => {
        applyBodyThemeClass();
        window.dispatchEvent(new Event("casinoThemeChange"));
    };

    useEffect(() => {
        originalModeRef.current = localStorage.getItem("darkModeStrategy");
        return () => {
            if (!savedRef.current) {
                // revert preview if user left without saving
                if (originalModeRef.current == null) {
                    localStorage.removeItem("darkModeStrategy");
                } else {
                    localStorage.setItem("darkModeStrategy", originalModeRef.current);
                }
                notifyThemeChange();
            }
        };
    }, []);

    const applyPreview = (mode) => {
        localStorage.setItem("darkModeStrategy", mode);
        notifyThemeChange();
    };

    useEffect(() => {
        if (hasLoaded) return; // prevent overriding user actions

        const targetMode = savedMode || byteToMode(darkMode);

        if (targetMode === "system") {
            setUseDeviceSettings(true);
        } else {
            setUseDeviceSettings(false);
            setTheme(targetMode);
        }

        setHasLoaded(true);
    }, [darkMode, savedMode, hasLoaded]);

    const byteToMode = (byte) => {
        switch (byte) {
            case 1: return "light";
            case 2: return "dark";
            case 3: return "system";
            default: return "light";
        }
    };

    const modeToByte = (mode) => {
        switch (mode) {
            case "light": return 1;
            case "dark": return 2;
            case "system": return 3;
            default: return 1;
        }
    };

    const handleLight = () => {
        if (!useDeviceSettings) {
            setTheme("light");
            applyPreview("light");
        }
    };

    const handleDark = () => {
        if (!useDeviceSettings) {
            setTheme("dark");
            applyPreview("dark");
        }
    };

    const handleToggle = () => {
        setUseDeviceSettings(prev => {
            const next = !prev;
            applyPreview(next ? "system" : theme);
            return next;
        });
    };

    const saveThemeSettings = () => {
        const mode = useDeviceSettings ? "system" : theme;

        localStorage.setItem("darkModeStrategy", mode);
        savedRef.current = true;
        notifyThemeChange();

        if (player) {
            const preferedMode = modeToByte(mode);
            casino.repos.igracDarkModeRepo(preferedMode); // sync to server
        }
    };

    return (
        <>
            {redirectToHome && (<Redirect to={"/"} />)}
            <Header key="header" {...props} />
            <div id="page-wrapper" className="casino-theme">
                <div className="theme-container">
                    <div className={`theme-selector ${theme}`}>
                        <div className="header">
                            <h1>Prilagodi izgled</h1>
                            <button className="close-button" onClick={() => setRedirectToHome(true)}>
                                <CloseIcon />
                            </button>
                        </div>

                        <p className="description">Prebaci SuperCasino iz svijetlog u tamni način zabave - tvoj izbor, tvoj stil.</p>

                        <div className="options-container">
                            <div onClick={handleLight} className={`theme-option ${theme === 'light' ? 'active' : ''} ${useDeviceSettings ? 'disabled' : ''}`}>
                                <div className="theme-option-label">
                                    <SunIcon />
                                    <span>Svijetla tema</span>
                                </div>
                                <div className="radio-button">
                                    {theme === 'light' && <CheckIcon />}
                                </div>
                            </div>
                            <div onClick={handleDark} className={`theme-option ${theme === 'dark' ? 'active' : ''} ${useDeviceSettings ? 'disabled' : ''}`}>
                                <div className="theme-option-label">
                                    <MoonIcon />
                                    <span>Tamna tema</span>
                                </div>
                                <div className="radio-button">
                                    {theme === 'dark' && <CheckIcon />}
                                </div>
                            </div>
                        </div>

                        <div className="divider-thick"></div>

                        <div className="device-settings">
                            <div className="device-settings-label">
                                <DeviceIcon />
                                <div className="device-settings-text">
                                    <p className="title">Prati postavke uređaja</p>
                                </div>
                            </div>
                            <div className="toggle-switch">
                                <input
                                    type="checkbox"
                                    name="toggle"
                                    id="device-settings-toggle"
                                    checked={useDeviceSettings}
                                    onChange={handleToggle}
                                    className="toggle-checkbox"
                                />
                                <label htmlFor="device-settings-toggle" className="toggle-label"></label>
                            </div>
                        </div>
                        <p className="device-settings-description">Tema se automatski prilagođava postavkama tvog uređaja za dosljedno iskustvo.</p>

                        <button className="save-button" onClick={() => {
                            setRedirectToHome(true);
                            saveThemeSettings();
                        }}>
                            SPREMI
                        </button>
                    </div>
                </div>
            </div>
        </>
    );
};

export default ThemeSelection;
